import {
  addDays,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  parse,
} from 'date-fns'

import { Value } from 'components/base/Form/Input'

import {
  Rule,
  Rules,
  Field,
  ValidateFieldResponses,
  ValidateFieldResponse,
  ValidateResponse,
  MultipleFieldsResponse,
  OptionValue,
  RuleOptions,
  RulesObject,
  SingleFieldResponse,
  ValidateFieldValidators,
  Validators,
} from './types'

export * from './types'

const singleFieldName = 'singleField'

export const validate = (ruleOrRules?: Rule | Rules, value?: Value): ValidateResponse => {
  const isSingleField = (!(ruleOrRules instanceof Array) && true) || false

  const rules: Rules = (ruleOrRules instanceof Array && ruleOrRules) || (typeof ruleOrRules === 'object' && ruleOrRules) || (ruleOrRules && [ ruleOrRules ]) || []

  const fields: Field[] = toFields(rules as Rules, value)

  const fieldResponses = validateFields(fields)

  const response = (isSingleField && {
    ...fieldResponses[singleFieldName],
    isSingleField: true,
  } as SingleFieldResponse) || {
    fields: fieldResponses,
    isSingleField: false,
    isValid: Object.values(fieldResponses).every((field: ValidateFieldResponse) => field.isValid),
  } as MultipleFieldsResponse

  return response
}

const toFields = (rules: Rules, value?: Value): Field[] => ([
  {
    name: singleFieldName,
    rules,
    value,
  },
])

const validateFields = (fields: Field[]): ValidateFieldResponses => fields.reduce((acc: ValidateFieldResponses, field: Field) => ({
  ...acc,
  [field.name]: validateField(field),
}), {})

const validateField = (field: Field): ValidateFieldResponse => {
  const rules = (field && parseRules(field.rules)) || {}

  const fieldValidators: ValidateFieldValidators = Object.entries(rules).reduce(
    (acc, [
      rule,
      options,
    ]) => {
      const validator = validators[rule]

      let isValid = undefined
      if (!validator) {
        isValid = true
      }

      isValid =
        ((typeof field.value === 'string' || typeof field.value === 'boolean') &&
          validator &&
          validator(field.value, options as RuleOptions)) ||
        false

      acc[rule] = { isValid }

      return acc
    },
    {},
  )

  return {
    isValid: Object.values(fieldValidators).every(validator => validator.isValid),
    rules: field.rules,
    validators: fieldValidators,
    value: field.value,
  }
}

export const parseRules = (rules: Rule | Rules): RulesObject => {
  let parsedRules: RulesObject = {}

  if (!rules) {
    return parsedRules
  }

  if (typeof rules === 'string') {
    parsedRules = rules.split('|').reduce(
      (acc, rule) =>
        Object.assign(acc, {
          [rule.split(':')[0]]:
            (rule.split(':')[1] &&
              rule
                .split(':')[1]
                .split(';')
                .reduce(
                  (options, option) => ({
                    ...options,
                    [option.split('=')[0]]: option.split('=')[1],
                  }),
                  {},
                )) ||
            true,
        }),
      {},
    )
  }
  else if (typeof rules === 'function') {
    parsedRules.func = { func: rules }
  }
  else if (rules instanceof Array) {
    return rules.map(item => parseRules(item)).reduce((acc, parsedRule) => ({
      ...acc,
      ...parsedRule,
    }), parsedRules)
  }
  else if (rules instanceof Object) {
    parsedRules = rules as RulesObject
  }

  parsedRules = Object.entries(parsedRules).reduce((acc, [
    rule,
    options,
  ]) => {
    acc[rule] =
      (typeof options === 'function' && options) ||
      (typeof options === 'object' && Object.entries(options as RuleOptions).reduce(
        (subAcc, [
          subRule,
          subRuleOptions,
        ]) => {
          subAcc[subRule] = fixOptionValue(subRule, subRuleOptions)

          return subAcc
        },
        {},
      )) || true

    return acc
  }, {})

  return parsedRules
}

const fixOptionValue = (option: string, optionValue: OptionValue): OptionValue => {
  const integerSubRules = [
    'min',
    'max',
    'minLength',
    'maxLength',
    'minDigits',
    'maxDigits',
  ]

  if (integerSubRules.find(subRule => subRule === option) && typeof optionValue === 'string') {
    return parseInt(optionValue)
  }
  else if (option === 'pattern' && typeof optionValue === 'string') {
    return new RegExp(optionValue)
  }

  return optionValue || undefined
}

export const validators: Validators = {
  required: value => (value && true) || (value === false) || false,

  date: (
    value,
    {
      after,
      afterOrEqual,
      before,
      beforeOrEqual,
    } = {},
  ) => {
    if (typeof value !== 'string') return false

    if (before && !isBefore(value as string, convertStringToDate(before) as Date)) {
      return false
    }

    if (beforeOrEqual && !(isBefore(value as string, convertStringToDate(beforeOrEqual) as Date) || isEqual(value as string, convertStringToDate(beforeOrEqual) as Date))) {
      return false
    }

    if (after && !isAfter(value as string, convertStringToDate(after) as Date)) {
      return false
    }

    if (afterOrEqual && !(isAfter(value as string, convertStringToDate(afterOrEqual) as Date) || isEqual(value as string, convertStringToDate(afterOrEqual) as Date))) {
      return false
    }

    return ((!value || value.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)) && isValid(new Date(value)) && true) || false
  },

  string: (
    value,
    {
      maxLength,
      minLength,
    } = {},
  ) => {
    if (minLength && value && value.toString().length < minLength) {
      return false
    }

    if (maxLength && value && value.toString().length > maxLength) {
      return false
    }

    return true
  },

  email: value =>
    ((typeof value !== 'string' || !value ||
      value.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      )) &&
      true) ||
    false,

  number: (
    value,
    {
      max,
      maxLength,
      min,
      minLength,
    } = {},
  ) => {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return false
    }

    if (value === undefined || value === '') {
      return true
    }

    if (!value.toString().match(/^-{0,1}[0-9,.]*$/)) {
      return false
    }

    if (minLength !== undefined && value.toString().length < minLength) {
      return false
    }

    if (maxLength !== undefined && value.toString().length > maxLength) {
      return false
    }

    if (min !== undefined && parseFloat(value as string) < min) {
      return false
    }

    if (max !== undefined && parseFloat(value as string) > max) {
      return false
    }

    return true
  },

  integer: (value, opts) => ((typeof value === 'string' || typeof value === 'number') && validators.number(value, opts) && (value.toString().match(/^-{0,1}[0-9]*$/)) && true) || false,

  phoneNumber: (
    value,
    {
      minDigits = 10,
      maxDigits = 10,
    } = {},
  ) => {
    if (typeof value !== 'string' || (value && !value.match(/^[0-9-.() +]*$/))) {
      return false
    }

    if (
      minDigits &&
      value &&
      value.toString().replace(/[^0-9]/g, '').length < minDigits
    ) {
      return false
    }

    if (
      maxDigits &&
      value &&
      value.toString().replace(/[^0-9]/g, '').length > maxDigits
    ) {
      return false
    }

    return true
  },

  match: (value, { pattern } = {}) => {
    if (typeof value !== 'string') return false

    if (typeof pattern === 'string') {
      pattern = new RegExp(pattern)
    }

    return (value.match(pattern as RegExp) && true) || false
  },

  func: (value, { func } = {}) => {
    if (!func) {
      return true
    }

    if (func.constructor.name === 'AsyncFunction') {
      // eslint-disable-next-line no-console
      console.error(
        'The `func` validation rule cannot accept async functions. To validate asynchronously, pass an async func as the `asyncValidate` prop to your Input. `asyncValidate` should be a sibling of `rules`.',
      )

      return true
    }

    return func(value)
  },
}

const convertStringToDate = (string: string): Date | undefined => {
  if (isValid(parse(string))) {
    return parse(string)
  }

  if (string === 'today' || string === 'tomorrow' || string === 'yesterday') {
    const today = parse(new Date().setHours(0, 0, 0, 0))

    if (string === 'tomorrow') {
      return addDays(today, 1)
    }
    else if (string === 'yesterday') {
      return addDays(today, -1)
    }

    return today
  }

  return undefined
}
